import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { Activity, Smile, Moon } from 'lucide-react';

interface VitalSignsData {
  total: number;
  byType: Array<{ name: string; value: number }> | null;
}

interface WellnessAnalyticsCardProps {
  vitalSigns: VitalSignsData | null;
  moodDistribution: Array<{ name: string; value: number }> | null;
  sleepQuality: Array<{ name: string; value: number }> | null;
  isLoading: boolean;
}

const VITAL_COLORS = [
  'hsl(var(--chart-1))',
  'hsl(var(--chart-2))',
  'hsl(var(--chart-3))',
  'hsl(var(--chart-4))',
  'hsl(var(--chart-5))',
  '#8884d8',
  '#82ca9d',
  '#ffc658',
];

const MOOD_COLORS: Record<string, string> = {
  'Excellent': 'hsl(142, 76%, 36%)',
  'Good': 'hsl(142, 71%, 45%)',
  'Neutral': 'hsl(217, 91%, 60%)',
  'Low': 'hsl(38, 92%, 50%)',
  'Very Low': 'hsl(0, 84%, 60%)',
};

const SLEEP_COLORS: Record<string, string> = {
  'Excellent': 'hsl(262, 83%, 58%)',
  'Good': 'hsl(217, 91%, 60%)',
  'Fair': 'hsl(38, 92%, 50%)',
  'Poor': 'hsl(25, 95%, 53%)',
  'Very Poor': 'hsl(0, 84%, 60%)',
};

export const WellnessAnalyticsCard: React.FC<WellnessAnalyticsCardProps> = ({
  vitalSigns,
  moodDistribution,
  sleepQuality,
  isLoading,
}) => {
  if (isLoading) {
    return (
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-primary" />
            Wellness Analytics
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Skeleton className="h-64" />
            <Skeleton className="h-64" />
            <Skeleton className="h-64" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-card border-border">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Activity className="h-5 w-5 text-primary" />
          Wellness Analytics
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Vital Signs Pie Chart */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Activity className="h-4 w-4" />
              Vital Signs by Type ({vitalSigns?.total || 0} total)
            </div>
            {vitalSigns?.byType && vitalSigns.byType.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={vitalSigns.byType}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={70}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {vitalSigns.byType.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={VITAL_COLORS[index % VITAL_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[220px] flex items-center justify-center text-muted-foreground">
                No vital sign data
              </div>
            )}
          </div>

          {/* Mood Distribution Bar Chart */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Smile className="h-4 w-4" />
              Mood Distribution
            </div>
            {moodDistribution && moodDistribution.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={moodDistribution} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" />
                  <YAxis dataKey="name" type="category" width={70} tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="value" name="Count">
                    {moodDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={MOOD_COLORS[entry.name] || 'hsl(var(--primary))'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[220px] flex items-center justify-center text-muted-foreground">
                No mood data
              </div>
            )}
          </div>

          {/* Sleep Quality Bar Chart */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Moon className="h-4 w-4" />
              Sleep Quality
            </div>
            {sleepQuality && sleepQuality.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={sleepQuality} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" />
                  <YAxis dataKey="name" type="category" width={70} tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="value" name="Count">
                    {sleepQuality.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={SLEEP_COLORS[entry.name] || 'hsl(var(--primary))'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[220px] flex items-center justify-center text-muted-foreground">
                No sleep data
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
